import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '@components/ui/Card';
import Button from '@components/ui/Button';
import Input from '@components/ui/Input';
import './CompanyForm.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FormState {
  name: string;
  website: string;
  sector: string;
  notes: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

const CompanyForm: React.FC = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>({
    name: '',
    website: '',
    sector: '',
    notes: '',
  });

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Partial<FormState>>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  // ─── Validation ──────────────────────────────────────────────────────────────

  const validate = (): boolean => {
    const newErrors: Partial<FormState> = {};
    if (!form.name.trim()) {
      newErrors.name = 'Company name is required.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ─── Submit ──────────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    setSaveError(null);

    try {
      const res = await fetch('/api/companies', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          website: form.website.trim() || null,
          sector: form.sector.trim() || null,
          notes: form.notes.trim() || null,
        }),
      });

      const json = await res.json() as {
        success: boolean;
        data?: { id: string };
        error?: string;
        message?: string;
        existing_id?: string;
      };

      if (res.status === 409) {
        // Duplicate company — offer to navigate to existing
        setSaveError(
          json.message ?? 'A company with this name already exists.'
        );
        if (json.existing_id) {
          // Navigate to the existing company
          navigate(`/companies/${json.existing_id}`);
        }
        return;
      }

      if (!res.ok || !json.success) {
        throw new Error(json.message ?? `HTTP ${res.status}`);
      }

      // Success — navigate to the new company detail page
      navigate(`/companies/${json.data!.id}`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create company.';
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="company-form-page">
      {/* Back navigation */}
      <button className="back-link" onClick={() => navigate('/companies')} type="button">
        ← Companies
      </button>

      {/* Page header */}
      <div className="form-page-header">
        <h1 className="form-page-title">New company</h1>
      </div>

      <Card>
        <form onSubmit={handleSubmit} noValidate>
          <div className="form-fields">
            <Input
              label="Company name *"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Acme Digital"
              error={errors.name}
              autoFocus
            />
            <Input
              label="Website"
              type="url"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              placeholder="https://example.com"
            />
            <Input
              label="Sector"
              value={form.sector}
              onChange={(e) => setForm({ ...form, sector: e.target.value })}
              placeholder="e.g. Technology, Healthcare, Education"
            />
            <div className="input-wrapper">
              <label className="input-label">Notes</label>
              <textarea
                className="input form-textarea"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="General notes about this company…"
                rows={5}
              />
            </div>

            {saveError && (
              <p className="form-save-error">{saveError}</p>
            )}

            <div className="form-actions">
              <Button
                type="submit"
                variant="primary"
                disabled={saving}
              >
                {saving ? 'Creating…' : 'Create company'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/companies')}
                disabled={saving}
              >
                Cancel
              </Button>
            </div>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default CompanyForm;
